import React, { createContext, useContext, useEffect, useMemo, useReducer } from "react";

const CartContext = createContext(null);

const getCartKey = (item) => {
  if (!item) return null;
  return item.variant_id ? `${item.id}:${item.variant_id}` : item.id;
};

const hydrateCart = (items = []) =>
  items
    .filter(Boolean)
    .map((item) => ({
      ...item,
      _cartKey: item._cartKey || getCartKey(item),
    }));

function cartReducer(state, action) {
  switch (action.type) {
    case "INIT":
      return hydrateCart(action.payload);
    case "ADD": {
      const incoming = { ...action.item };
      const cartKey = getCartKey(incoming);
      const existing = state.find((p) => p._cartKey === cartKey);
      const next = existing
        ? state.map((p) =>
            p._cartKey === cartKey ? { ...p, qty: p.qty + (action.qty || 1) } : p
          )
        : [...state, { ...incoming, _cartKey: cartKey, qty: action.qty || 1 }];
      localStorage.setItem("pp_cart", JSON.stringify(next));
      return next;
    }
    case "REMOVE": {
      const target = action.cartKey || action.id;
      const next = state.filter((i) => i._cartKey !== target && i.id !== target);
      localStorage.setItem("pp_cart", JSON.stringify(next));
      return next;
    }
    case "SET_QTY": {
      const target = action.cartKey || action.id;
      const next = state.map((i) =>
        i._cartKey === target || i.id === target ? { ...i, qty: Math.max(1, action.qty) } : i
      );
      localStorage.setItem("pp_cart", JSON.stringify(next));
      return next;
    }
    case "CLEAR":
      localStorage.removeItem("pp_cart");
      return [];
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [cart, dispatch] = useReducer(cartReducer, []);

  useEffect(() => {
    const saved = localStorage.getItem("pp_cart");
    if (saved) dispatch({ type: "INIT", payload: JSON.parse(saved) });
  }, []);

  const total = useMemo(() => cart.reduce((sum, item) => sum + item.price_sek * item.qty, 0), [cart]);

  const value = useMemo(
    () => ({
      cart,
      total,
      addItem: (item, qty = 1) => dispatch({ type: "ADD", item, qty }),
      removeItem: (id) => dispatch({ type: "REMOVE", id }),
      setQty: (id, qty) => dispatch({ type: "SET_QTY", id, qty }),
      clearCart: () => dispatch({ type: "CLEAR" }),
    }),
    [cart, total]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);

